import { useState, useEffect } from 'react';
import { Check, X, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { toast } from '@/components/ui/sonner';
import { usePersonaChanges, useRefinePersona, useValidatePersonaExamples } from '@/hooks/use-personas';
import { PersonaChangesPanel } from './persona-changes-panel';
import { PersonaSummaryContent } from './persona-summary-content';
import { cn } from '@/lib/utils';
import type { Persona, PersonaExample, PersonaExampleResponse } from '@/types';

interface PersonaValidationFlowProps {
	persona: Persona;
	label: string;
	onProgressChange: (hasProgress: boolean) => void;
	onValidated: () => void;
	onRegenerationStart: (kind: 'regenerate' | 'update') => void;
}

type SummaryVerdict = 'looks_good' | 'respond_differently' | null;

interface ExampleVerdict {
	response: PersonaExampleResponse;
	alternativeText: string;
}

export function PersonaValidationFlow({ persona, onProgressChange, onValidated, onRegenerationStart }: PersonaValidationFlowProps) {
	const [stage, setStage] = useState<'summary' | 'examples'>('summary');
	const [summaryVerdict, setSummaryVerdict] = useState<SummaryVerdict>(null);
	const [summaryFeedback, setSummaryFeedback] = useState('');
	const [stepIndex, setStepIndex] = useState(0);
	const [verdicts, setVerdicts] = useState<Record<string, ExampleVerdict>>({});

	const refinePersona = useRefinePersona();
	const validateExamples = useValidatePersonaExamples();
	const { data: changes } = usePersonaChanges(persona.id);

	const examples = persona.examples;
	const currentExample: PersonaExample | undefined = examples[stepIndex];
	const currentVerdict = currentExample ? verdicts[currentExample.exampleId] : undefined;

	useEffect(() => {
		const hasProgress =
			(stage === 'summary' && (summaryVerdict !== null || summaryFeedback.trim().length > 0)) ||
			(stage === 'examples' && (stepIndex > 0 || Object.keys(verdicts).length > 0));
		onProgressChange(hasProgress);
	}, [stage, summaryVerdict, summaryFeedback, stepIndex, verdicts, onProgressChange]);

	const handleRegenerateFromSummary = async () => {
		if (!summaryFeedback.trim()) {
			toast.error('Provide feedback first');
			return;
		}
		try {
			await refinePersona.mutateAsync({ id: persona.id, feedbackText: summaryFeedback });
			onRegenerationStart('regenerate');
			setSummaryVerdict(null);
			setSummaryFeedback('');
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Failed to start regeneration');
		}
	};

	// Opens the summary's "Doesn't look good" feedback box with the suggestion on its own line, so several can go in
	// one regenerate. A suggestion already in the box is not added twice.
	const handleUseSuggestion = (suggestion: string) => {
		setSummaryVerdict('respond_differently');
		setSummaryFeedback((current) => {
			if (current.includes(suggestion)) return current;
			return current.trim() ? `${current.trim()}\n${suggestion}` : suggestion;
		});
	};
	// What the panel treats as already added: only text in the visible feedback box counts.
	const visibleFeedback = summaryVerdict === 'respond_differently' ? summaryFeedback : '';

	const setCurrentVerdict = (response: PersonaExampleResponse, alternativeText = '') => {
		if (!currentExample) return;
		setVerdicts((prev) => ({ ...prev, [currentExample.exampleId]: { response, alternativeText } }));
	};

	const handleFinish = async () => {
		const payload = examples
			.filter((example) => verdicts[example.exampleId])
			.map((example) => ({
				exampleId: example.exampleId,
				response: verdicts[example.exampleId]!.response,
				alternativeText:
					verdicts[example.exampleId]!.response === 'respond_differently'
						? verdicts[example.exampleId]!.alternativeText
						: undefined
			}));

		try {
			const result = await validateExamples.mutateAsync({ id: persona.id, examples: payload });
			if (result.persona) {
				onValidated();
			} else {
				onRegenerationStart('update');
			}
			setVerdicts({});
			setStepIndex(0);
			setStage('summary');
			setSummaryVerdict(null);
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Failed to submit validation');
		}
	};

	if (stage === 'summary') {
		return (
			<div className='flex flex-col gap-4'>
				{changes && (
					<PersonaChangesPanel changes={changes} feedback={visibleFeedback} onUseSuggestion={handleUseSuggestion} />
				)}
				<PersonaSummaryContent personaId={persona.id} />

				<div className='sticky bottom-0 rounded-2xl border bg-white/80 backdrop-blur-[6px] p-4 shadow-[0px_-3px_12px_0px_rgba(0,0,0,0.12)] flex flex-col gap-3'>
					<div>
						<h3 className='text-sm font-medium'>Does this sound right ?</h3>
						<p className='text-xs text-muted-foreground'>
							If it does, we'll present you with several examples to help you review and confirm the scenarios,
							ensuring the behavior is saved.
						</p>
					</div>

					<div className='flex gap-2'>
						<Button
							variant='outline'
							className={cn(
								'rounded-full gap-1.5 font-normal',
								summaryVerdict === 'looks_good' && 'border-green-600 bg-green-50 text-green-700'
							)}
							onClick={() => setSummaryVerdict('looks_good')}
						>
							<Check className='size-4' />
							Looks good
						</Button>
						<Button
							variant='outline'
							className={cn(
								'rounded-full gap-1.5 font-normal',
								summaryVerdict === 'respond_differently' && 'border-destructive bg-destructive/10 text-destructive'
							)}
							onClick={() => setSummaryVerdict('respond_differently')}
						>
							<X className='size-4' />
							Doesn't Look Good
						</Button>
					</div>

					{summaryVerdict === 'respond_differently' && (
						<>
							<Separator />
							<div className='flex flex-col gap-1.5'>
								<label className='text-xs font-medium'>Feedback</label>
								<Textarea
									placeholder='Provide feedback...'
									value={summaryFeedback}
									onChange={(e) => setSummaryFeedback(e.target.value)}
									rows={3}
									className='resize-none rounded-2xl'
								/>
							</div>
							<Separator />
							<div className='flex justify-end gap-2'>
								<Button variant='outline' className='rounded-full font-normal' onClick={() => setSummaryVerdict(null)}>
									Cancel
								</Button>
								<Button
									className='rounded-full font-normal'
									onClick={handleRegenerateFromSummary}
									disabled={refinePersona.isPending}
								>
									Regenerate Persona
								</Button>
							</div>
						</>
					)}

					{summaryVerdict === 'looks_good' && (
						<>
							<Separator />
							<div className='flex justify-end'>
								<Button className='rounded-full font-normal' onClick={() => setStage('examples')}>
									Next, Validate Examples
								</Button>
							</div>
						</>
					)}
				</div>
			</div>
		);
	}

	if (!currentExample) {
		return <p className='text-xs text-muted-foreground'>No examples to validate.</p>;
	}

	const isLastStep = stepIndex === examples.length - 1;

	return (
		<div className='flex flex-col gap-4 flex-1 min-h-0'>
			<Accordion type='single' collapsible>
				<AccordionItem value='summary'>
					<AccordionTrigger
						className='text-xs font-medium [&[data-state=open]>svg]:rotate-0'
						chevronClassName='-rotate-90'
					>
						Summary
					</AccordionTrigger>
					<AccordionContent>
						<PersonaSummaryContent personaId={persona.id} showLabel={false} />
					</AccordionContent>
				</AccordionItem>
			</Accordion>

			<Separator className='-mx-4 data-[orientation=horizontal]:w-[calc(100%+2rem)]' />

			<div className='rounded-3xl border bg-white shadow-sm flex flex-col gap-4 flex-1 min-h-0 overflow-hidden'>
				<div className='flex items-center justify-between px-4 pt-4'>
					<h3 className='text-sm font-medium'>Validate examples</h3>
					<span className='text-xs text-muted-foreground'>
						{stepIndex + 1}/{examples.length}
					</span>
				</div>

				<div className='flex flex-col flex-1 min-h-0 overflow-y-auto custom-scrollbar'>
					<div className='flex flex-col gap-6 px-4 pb-4'>
						<div className='flex gap-2 items-start pr-16'>
							<div className='size-8 shrink-0 rounded-full bg-foreground flex items-center justify-center'>
								<User className='size-4 text-background' />
							</div>
							<div className='flex-1 min-w-0 flex flex-col gap-2'>
								<div className='rounded-3xl border bg-white p-3'>
									<p className='text-sm'>{currentExample.query}</p>
								</div>
								<p className='text-[11px] font-medium text-muted-foreground'>Customer Query</p>
							</div>
						</div>
						<div className='flex gap-2 items-start justify-end pl-16'>
							<div className='flex-1 min-w-0'>
								<div className='rounded-3xl border bg-blue-50 p-3'>
									<p className='text-sm whitespace-pre-wrap'>{currentExample.reply}</p>
								</div>
							</div>
							<img src='/assets/persona/ai-sparkle.svg' alt='' className='size-8 shrink-0' />
						</div>
					</div>

					<div className='sticky bottom-0 mt-auto flex flex-col gap-3 border-t bg-white/80 px-4 py-3 backdrop-blur-[4px]'>
						<div>
							<h4 className='text-sm font-medium'>Does the response look good ?</h4>
							<div className='flex gap-2 mt-2'>
								<Button
									variant='outline'
									className={cn(
										'rounded-full gap-1.5 font-normal',
										currentVerdict?.response === 'looks_good' && 'border-green-600 bg-green-50 text-green-700'
									)}
									onClick={() => setCurrentVerdict('looks_good')}
								>
									<Check className='size-4' />
									Looks good
								</Button>
								<Button
									variant='outline'
									className={cn(
										'rounded-full gap-1.5 font-normal',
										currentVerdict?.response === 'respond_differently' && 'border-destructive bg-destructive/10 text-destructive'
									)}
									onClick={() => setCurrentVerdict('respond_differently')}
								>
									<X className='size-4' />
									I'd respond differently
								</Button>
							</div>
						</div>

						{currentVerdict?.response === 'respond_differently' && (
							<div className='flex flex-col gap-1.5'>
								<label className='text-xs font-medium'>Would you respond differently?</label>
								<Textarea
									value={currentVerdict.alternativeText}
									onChange={(e) => setCurrentVerdict('respond_differently', e.target.value)}
									rows={4}
									className='resize-none rounded-2xl'
								/>
							</div>
						)}

						<Separator />
						<div className='flex justify-end gap-2'>
							<Button
								variant='outline'
								className='rounded-full font-normal'
								onClick={() => (stepIndex === 0 ? setStage('summary') : setStepIndex((i) => i - 1))}
							>
								Back
							</Button>
							{isLastStep ? (
								<Button
									className='rounded-full font-normal'
									disabled={!currentVerdict || validateExamples.isPending}
									onClick={handleFinish}
								>
									Finish
								</Button>
							) : (
								<Button
									className='rounded-full font-normal'
									disabled={!currentVerdict}
									onClick={() => setStepIndex((i) => Math.min(examples.length - 1, i + 1))}
								>
									Next
								</Button>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
